// bluaIQ — P1: Voice Input
// Reusable mic button using Web Speech API for bilingual voice-to-text

import { useState, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Mic, MicOff } from 'lucide-react'

// ── TYPES ─────────────────────────────────────────

interface VoiceInputProps {
  onTranscript: (text: string) => void
  lang?: 'en-US' | 'es-MX'
  placeholder?: string
}

interface SpeechRecognitionEvent {
  results: { [index: number]: { [index: number]: { transcript: string } } }
  resultIndex: number
}

// ── COMPONENT ─────────────────────────────────────

export default function VoiceInput({ onTranscript, lang, placeholder }: VoiceInputProps) {
  const { i18n } = useTranslation()
  const isEs = i18n.language === 'es'
  const speechLang = lang || (isEs ? 'es-MX' : 'en-US')

  const [isRecording, setIsRecording] = useState(false)
  const [transcript, setTranscript] = useState('')
  const recognitionRef = useRef<ReturnType<typeof createRecognition> | null>(null)

  const createRecognition = useCallback(() => {
    const SpeechRecognition = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition
    if (!SpeechRecognition) return null

    const recognition = new SpeechRecognition()
    recognition.lang = speechLang
    recognition.interimResults = true
    recognition.continuous = true
    return recognition
  }, [speechLang])

  const startRecording = useCallback(() => {
    const recognition = createRecognition()
    if (!recognition) return

    recognitionRef.current = recognition

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let finalTranscript = ''
      let interimTranscript = ''
      for (let i = event.resultIndex; i < Object.keys(event.results).length; i++) {
        const result = event.results[i]
        if (result[0]) {
          const text = result[0].transcript
          if ((result as any).isFinal) {
            finalTranscript += text
          } else {
            interimTranscript += text
          }
        }
      }

      if (finalTranscript) {
        setTranscript(prev => {
          const updated = prev ? `${prev} ${finalTranscript}` : finalTranscript
          onTranscript(updated)
          return updated
        })
      } else {
        setTranscript(prev => prev ? `${prev} ${interimTranscript}` : interimTranscript)
      }
    }

    recognition.onerror = () => {
      setIsRecording(false)
    }

    recognition.onend = () => {
      setIsRecording(false)
    }

    recognition.start()
    setIsRecording(true)
  }, [createRecognition, onTranscript])

  const stopRecording = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop()
      recognitionRef.current = null
    }
    setIsRecording(false)
  }, [])

  const toggleRecording = () => {
    if (isRecording) {
      stopRecording()
    } else {
      setTranscript('')
      startRecording()
    }
  }

  const hasSupport = typeof window !== 'undefined' && ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)

  if (!hasSupport) return null

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={toggleRecording}
        className={`flex items-center justify-center w-[36px] h-[36px] rounded-full transition-all ${
          isRecording
            ? 'bg-[#EF4444] text-white animate-pulse'
            : 'bg-[#F2F0EB] text-[#1C1A17] hover:bg-[#ECEAE4]'
        }`}
        title={isRecording
          ? (isEs ? 'Detener grabación' : 'Stop recording')
          : (isEs ? 'Iniciar grabación' : 'Start recording')
        }
      >
        {isRecording ? <MicOff size={16} /> : <Mic size={16} />}
      </button>

      {(isRecording || transcript) && (
        <div className="flex-1 min-w-0">
          <div className={`text-[11px] font-[600] px-3 py-2 rounded-lg border ${
            isRecording
              ? 'border-[#EF4444]/30 bg-red-50 text-[#1C1A17]'
              : 'border-[#ECEAE4] bg-white text-[#1C1A17]'
          }`}>
            {transcript || (
              <span className="text-[#9E9890]">
                {placeholder || (isEs ? 'Escuchando...' : 'Listening...')}
              </span>
            )}
          </div>
          {isRecording && (
            <div className="text-[9px] font-[600] text-[#EF4444] mt-1">
              {isEs ? 'Grabando...' : 'Recording...'}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
